import { ApolloClient, ApolloLink, InMemoryCache } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { createUploadLink } from "apollo-upload-client";

import { getRequestId } from "@compono/sentry-browser";
import { apolloSpy } from "@shortlyster/analytics-kit";

import { getCachedAuthContext } from "~/components/auth";
import { graphqlErrorLink } from "~/utils/graphqlErrorLink";

import { watcher } from "../mixpanel";
import customFetch from "./customFetch";

export type UseApolloClientProps = {
  uri: string;
};

export const apolloClientWithAuth0 = ({ uri }: UseApolloClientProps) => {
  const authLink = setContext(async (_, { headers, ...auth }) => {
    const accessToken = getCachedAuthContext()?.user?.access_token;
    const orgId = getCachedAuthContext()?.orgId;
    return {
      ...auth,
      headers: {
        ...headers,
        ...(accessToken && { authorization: `Bearer ${accessToken}` }),
        ...(orgId && { "x-organisation-id": orgId }),
        "x-request-id": getRequestId(),
      },
    };
  });

  // https://www.apollographql.com/docs/react/migrating/apollo-client-3-migration/#breaking-cache-changes
  // https://www.apollographql.com/docs/react/data/fragments/#defining-possibletypes-manually
  const cache = new InMemoryCache({
    possibleTypes: {
      Answer: ["FloatAnswer", "StringAnswer"],
      Feed: ["ChangeLog", "Note"],
    },
  });

  const uploadLink = createUploadLink({ uri, fetch: customFetch });

  const baseClient = new ApolloClient({
    link: ApolloLink.from([graphqlErrorLink, authLink, uploadLink as any]),
    cache,
  });

  apolloSpy.configure({ watcher });

  const client = apolloSpy.patch(baseClient);
  return client;
};
